// ===================================================================
// PrescriptionWizard — emitir una receta electrónica, paso a paso
// ===================================================================
//
// Reemplaza al formulario anterior, donde el medicamento se tipeaba a mano.
// Ahora se elige del catálogo de Qbi2, que además dice si es psicofármaco,
// si requiere duplicado y si la obra social lo cubre.
//
// El último paso antes de emitir es una revisión que muestra la receta tal
// como va a salir. Importa más de lo que parece: una receta emitida entra al
// repositorio de Farmalink y no se borra, sólo se anula — y la anulación
// probablemente también cuenta contra el plan mensual. Por eso el paso de
// revisión es explícito y el botón de emitir avisa que no tiene vuelta atrás.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, User, Stethoscope, Pill, FileCheck, Check, ChevronLeft, ChevronRight,
  Search, Loader2, AlertTriangle, AlertCircle, Plus, Trash2, Sparkles,
  ShieldCheck, Copy, BadgeCheck, FileText, Share2,
} from 'lucide-react';
import SignaturePad from './SignaturePad';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ===================================================================
// Tipos
// ===================================================================

/** Lo que devuelve el catálogo de Qbi2 por cada medicamento. */
interface MedicamentoCatalogo {
  nombreProducto: string;
  nombreDroga: string;
  presentacion: string;
  formaFarmaceutica: string;
  potencia: string;
  descripcionPotencia: string;
  viaAdministracion: string;
  regNo: string;
  tieneCobertura: boolean;
  descuento: number;
  psicofarmaco: boolean;
  estupefaciente: boolean;
  ventaControlada: boolean;
  requiereDuplicado: boolean;
  hiv: boolean;
  descripcionTipoExpendio: string;
}

interface DiagnosticoCatalogo {
  iddiagnostico: number;
  coddiagnostico: string;
  descdiagnostico: string;
}

/**
 * Obra social o prepaga del catálogo de Qbi2.
 *
 * La elige el profesional a mano y no se deduce del padrón: PUCO devuelve el
 * RNOS pero este catálogo no lo expone, así que cruzarlos sería adivinar por
 * nombre entre 835 candidatos. En una receta, errarle significa mostrarle al
 * paciente el descuento de otra obra social.
 */
interface Financiador {
  idfinanciador: number;
  nombreComercial: string;
}

/**
 * Un renglón de la receta.
 *
 * Los ocho primeros campos son los que espera el backend y no se tocan. El
 * `catalogo` queda sólo del lado del navegador para mostrar las advertencias:
 * no se envía, así el mapper del backend sigue recibiendo exactamente lo mismo
 * que antes.
 */
interface Renglon {
  medication: string;
  commercialName: string;
  pharmaceuticalForm: string;
  presentation: string;
  quantity: number;
  dosage: string;
  duration: string;
  notes: string;
  catalogo?: MedicamentoCatalogo;
}

const RENGLON_VACIO: Renglon = {
  medication: '', commercialName: '', pharmaceuticalForm: '', presentation: '',
  quantity: 1, dosage: '', duration: '', notes: '',
};

const PASOS = [
  { n: 1, titulo: 'Paciente', icono: User },
  { n: 2, titulo: 'Diagnóstico', icono: Stethoscope },
  { n: 3, titulo: 'Medicamentos', icono: Pill },
  { n: 4, titulo: 'Revisión', icono: FileCheck },
];

// ===================================================================
// Barra de pasos
// ===================================================================
const BarraPasos: React.FC<{ actual: number }> = ({ actual }) => (
  <div className="flex items-center px-5 py-4 bg-gradient-to-r from-[#0FA3B1]/8 to-[#30B0C7]/8 border-b border-[#0FA3B1]/15">
    {PASOS.map((p, i) => {
      const hecho = actual > p.n;
      const activo = actual === p.n;
      const Icono = hecho ? Check : p.icono;
      return (
        <React.Fragment key={p.n}>
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                hecho ? 'bg-emerald-500 text-white'
                : activo ? 'bg-[#0FA3B1] text-white ring-4 ring-[#0FA3B1]/20'
                : 'bg-white text-gray-500 border border-gray-200'
              }`}
            >
              <Icono className="w-4 h-4" />
            </div>
            <span
              className={`text-[13px] font-medium truncate hidden sm:block ${
                activo ? 'text-[#0B7C87]' : hecho ? 'text-emerald-600' : 'text-gray-500'
              }`}
            >
              {p.titulo}
            </span>
          </div>
          {i < PASOS.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 rounded ${actual > p.n ? 'bg-emerald-400' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ===================================================================
// Buscador con autocompletado
//
// Espera 350 ms desde la última tecla antes de consultar, y descarta las
// respuestas que llegan fuera de orden: si el profesional escribe rápido,
// una consulta vieja puede volver después de una nueva y pisar la lista.
// ===================================================================
function useBuscador<T>(url: (q: string) => string, extraer: (j: any) => T[]) {
  const [resultados, setResultados] = useState<T[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const pedidoNro = useRef(0);

  const buscar = useCallback((texto: string) => {
    const q = texto.trim();
    if (q.length < 3) { setResultados([]); setBuscando(false); return; }

    const mio = ++pedidoNro.current;
    setBuscando(true);
    const token = localStorage.getItem('token');

    fetch(`${API_URL}${url(q)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.json())
      .then((j) => {
        if (mio !== pedidoNro.current) return; // llegó tarde, ya hay una búsqueda más nueva
        setResultados(extraer(j) || []);
        setAviso(j.aviso || null);
      })
      .catch(() => { if (mio === pedidoNro.current) setResultados([]); })
      .finally(() => { if (mio === pedidoNro.current) setBuscando(false); });
  }, [url, extraer]);

  return { resultados, buscando, aviso, buscar, limpiar: () => setResultados([]) };
}

// ===================================================================
// Etiquetas de advertencia de un medicamento
// ===================================================================
const Avisos: React.FC<{ m: MedicamentoCatalogo; coberturaVerificada?: boolean }> = ({
  m, coberturaVerificada,
}) => {
  const etiquetas: Array<{ texto: string; clase: string }> = [];
  if (m.estupefaciente) etiquetas.push({ texto: 'Estupefaciente', clase: 'bg-red-100 text-red-700 border-red-200' });
  if (m.psicofarmaco) etiquetas.push({ texto: 'Psicofármaco', clase: 'bg-orange-100 text-orange-700 border-orange-200' });
  if (m.requiereDuplicado) etiquetas.push({ texto: 'Requiere duplicado', clase: 'bg-amber-100 text-amber-700 border-amber-200' });
  if (m.ventaControlada) etiquetas.push({ texto: 'Venta controlada', clase: 'bg-amber-100 text-amber-700 border-amber-200' });

  // La cobertura sólo se muestra cuando la búsqueda se hizo con una obra
  // social elegida. Sin `idFinanciador`, el catálogo devuelve
  // `tieneCobertura: true` para todo y `descuento` en 0: no significa "el
  // plan de este paciente lo cubre" sino "es cubrible en general". Mostrarlo
  // así sería prometerle al profesional algo que nadie verificó, y él se lo
  // diría al paciente.
  if (coberturaVerificada) {
    if (m.tieneCobertura) {
      etiquetas.push({
        texto: m.descuento > 0 ? `Cubierto ${Math.round(m.descuento)}%` : 'Con cobertura',
        clase: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      });
    } else {
      etiquetas.push({ texto: 'Sin cobertura', clase: 'bg-gray-100 text-gray-700 border-gray-200' });
    }
  }

  if (etiquetas.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {etiquetas.map((e) => (
        <span key={e.texto} className={`text-[12px] font-semibold px-2 py-0.5 rounded-full border ${e.clase}`}>
          {e.texto}
        </span>
      ))}
    </div>
  );
};

// ===================================================================
// Campo con etiqueta
// ===================================================================
const Campo: React.FC<{
  label: string; children: React.ReactNode; className?: string; requerido?: boolean;
}> = ({ label, children, className = '', requerido }) => (
  <div className={className}>
    <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">
      {label} {requerido && <span className="text-red-400">*</span>}
    </label>
    {children}
  </div>
);

const claseInput =
  'w-full px-3 py-2.5 text-[15px] text-gray-900 border border-gray-300 rounded-xl placeholder:text-gray-500 focus:ring-2 focus:ring-[#0FA3B1] focus:border-transparent outline-none transition';

// ===================================================================
// Wizard
// ===================================================================
/** Datos del paciente que vienen de otra pantalla, para no tipearlos de nuevo. */
export interface PacienteInicial {
  name?: string;
  dni?: string;
  sex?: '' | 'F' | 'M' | 'X';
  birthDate?: string;
  healthInsurance?: string;
  insuranceNumber?: string;
}

const PrescriptionWizard: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Paciente prellenado, normalmente desde el turno abierto en el calendario.
   * La receta se escribe durante la consulta: si el nombre y el DNI ya están
   * en pantalla, volver a tipearlos es trabajo regalado y una oportunidad más
   * de equivocarse en el documento.
   */
  pacienteInicial?: PacienteInicial | null;
}> = ({ isOpen, onClose, onSuccess, pacienteInicial }) => {
  const [paso, setPaso] = useState(1);
  const [emitiendo, setEmitiendo] = useState(false);
  const [error, setError] = useState('');

  const [patient, setPatient] = useState({
    name: '', dni: '', sex: '' as '' | 'F' | 'M' | 'X',
    birthDate: '', healthInsurance: '', insuranceNumber: '',
  });
  const [diagnosis, setDiagnosis] = useState({ code: '', description: '' });
  const [financiador, setFinanciador] = useState<Financiador | null>(null);
  // Ficha de la que salieron los datos, si el profesional eligió un paciente
  // suyo. Sirve para devolverle la obra social y que no la elija de nuevo.
  const [fichaId, setFichaId] = useState<number | null>(null);

  /**
   * Guarda la obra social elegida en la ficha del paciente.
   *
   * Es el punto de todo esto: se elige una vez y queda. Falla en silencio a
   * propósito — si no se puede guardar, la receta sigue su curso igual; sería
   * absurdo trabar una emisión por no poder recordar una preferencia.
   */
  const guardarEnFicha = async (campos: Record<string, string>) => {
    if (!fichaId) return;
    try {
      const token = localStorage.getItem('token');
      const profId = JSON.parse(localStorage.getItem('user') || '{}').id;
      await fetch(`${API_URL}/api/patient-records/professional/${profId}/${fichaId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(campos),
      });
    } catch { /* que no moleste: la receta sigue igual */ }
  };

  const recordarObraSocial = async (f: Financiador | null) => {
    setFinanciador(f);
    if (!f) return;
    await guardarEnFicha({
      obra_social_id: String(f.idfinanciador),
      obra_social_nombre: f.nombreComercial,
    });
  };

  /**
   * El número de afiliado no lo puede traer ningún registro: PUCO devuelve la
   * obra social pero no el número, y no hay padrón nacional que lo tenga.
   * Está en el carnet del paciente y nada más.
   *
   * Por eso, la única forma de no pedirlo cada vez es guardarlo la primera vez
   * que alguien lo tipea. Se preguntaba en cada receta y se perdía.
   */
  const recordarNroAfiliado = async (numero: string) => {
    const limpio = numero.trim();
    if (!limpio) return;
    await guardarEnFicha({ nro_afiliado: limpio });
  };
  const [renglones, setRenglones] = useState<Renglon[]>([{ ...RENGLON_VACIO }]);
  const [signature, setSignature] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{
    cuir: string;
    qrCode: string;
    emision?: {
      registrada: boolean;
      cuir: string | null;
      idReceta: string | null;
      pdfOficial: string | null;
      verificador: string | null;
    };
  } | null>(null);

  const reiniciar = () => {
    setPaso(1); setError(''); setEmitiendo(false);
    setPatient({ name: '', dni: '', sex: '', birthDate: '', healthInsurance: '', insuranceNumber: '' });
    setDiagnosis({ code: '', description: '' });
    setFinanciador(null);
    setFichaId(null);
    setRenglones([{ ...RENGLON_VACIO }]);
    setSignature(null); setResultado(null);
  };

  // Al cerrar después de emitir se avisa al padre para que refresque la lista.
  // El aviso NO va al momento de emitir: el padre cierra el modal cuando lo
  // recibe, y el profesional se quedaría sin ver el CUIR.
  const cerrar = () => {
    const emitida = Boolean(resultado);
    reiniciar();
    if (emitida) onSuccess(); else onClose();
  };

  // -----------------------------------------------------------------
  // Validación por paso
  // -----------------------------------------------------------------
  const problemasPaso = (n: number): string[] => {
    const p: string[] = [];
    if (n === 1) {
      if (!patient.name.trim()) p.push('Falta el nombre del paciente');
      if (!patient.sex) p.push('Falta el sexo (lo exige el Decreto 63/2024)');
      if (patient.dni && patient.dni.replace(/\D/g, '').length < 6) p.push('El DNI parece incompleto');
    }
    if (n === 3) {
      const cargados = renglones.filter((r) => r.medication.trim());
      if (cargados.length === 0) p.push('Agregá al menos un medicamento');
      cargados.forEach((r, i) => {
        if (!r.pharmaceuticalForm.trim()) {
          p.push(`Al medicamento ${i + 1} le falta la forma farmacéutica (la exige el Decreto 63/2024)`);
        }
      });
    }
    return p;
  };

  const avanzar = () => {
    const p = problemasPaso(paso);
    if (p.length) { setError(p[0]); return; }
    setError(''); setPaso(paso + 1);
  };

  // -----------------------------------------------------------------
  // Emitir
  // -----------------------------------------------------------------
  const emitir = async () => {
    if (!signature) { setError('Firmá la receta antes de emitirla'); return; }
    setEmitiendo(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/prescriptions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          // `financiadorId` es el id del catálogo de Qbi2 y viaja aparte del
          // nombre de la obra social: del otro lado la cobertura se identifica
          // por id, no por texto.
          patient: { ...patient, financiadorId: financiador?.idfinanciador ?? null },
          // Ficha del paciente, si vino de la lista. Es lo que enlaza la receta
          // con el paciente en la sección Pacientes; sin esto el contador de
          // recetas de esa pantalla queda siempre en cero.
          patientRecordId: fichaId,
          diagnosis,
          // El objeto `catalogo` entero no viaja (es para la pantalla), pero sí
          // los cuatro campos que Qbi2 necesita para saber exactamente qué
          // producto es. Sin el `regNo` solo puede adivinar por nombre.
          prescriptions: renglones
            .filter((x) => x.medication.trim())
            .map(({ catalogo, ...resto }) =>
              catalogo
                ? {
                    ...resto,
                    qbi2: {
                      regNo: catalogo.regNo,
                      nombreProducto: catalogo.nombreProducto,
                      nombreDroga: catalogo.nombreDroga,
                      presentacion: catalogo.presentacion,
                    },
                  }
                : resto
            ),
          signature,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'No se pudo emitir la receta');
      setResultado({
        cuir: j.prescription.cuir,
        qrCode: j.prescription.qrCode,
        // Trae el número real, el PDF oficial de Qbi2 y el link verificador.
        emision: j.emision,
      });
      setPaso(5);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error desconocido');
    } finally {
      setEmitiendo(false);
    }
  };

  // Prellena el paciente al abrirse, cuando el asistente se lanza desde un
  // turno. Va atado a `isOpen` y no a `pacienteInicial`: así se carga una vez
  // al abrir y no vuelve a pisar lo que el profesional haya corregido a mano.
  useEffect(() => {
    if (!isOpen || !pacienteInicial) return;
    setPatient((actual) => ({
      ...actual,
      name: pacienteInicial.name || actual.name,
      dni: (pacienteInicial.dni || actual.dni || '').replace(/\D/g, ''),
      sex: pacienteInicial.sex || actual.sex,
      birthDate: pacienteInicial.birthDate || actual.birthDate,
      healthInsurance: pacienteInicial.healthInsurance || actual.healthInsurance,
      insuranceNumber: pacienteInicial.insuranceNumber || actual.insuranceNumber,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const medicamentosCargados = renglones.filter((r) => r.medication.trim());

  return (
    // z-[110] y no z-50: el encabezado de la app es sticky con z-100, asi que
    // con z-50 el modal quedaba cortado arriba y, con el menu desplegado, la X
    // de cerrar quedaba tapada. Los otros dos modales de la app tienen el mismo
    // problema y habria que subirlos igual.
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      {/* text-left a propósito: la pantalla que monta este modal centra todo su
          contenido, y el centrado se hereda. Una receta es un documento y se
          lee alineada a la izquierda. */}
      {/* En escritorio el modal era `sm:h-auto`, o sea que se achicaba al alto
          del contenido. Los pasos con poco contenido —el de diagnóstico, que
          es un párrafo y un buscador— quedaban de 150px, y el desplegable de
          resultados lo recortaba el `overflow-y-auto` del cuerpo: se tipeaba y
          no se veían las coincidencias.
          El min-h le reserva lugar; el min() evita pasarse en pantallas bajas. */}
      <div className="bg-white w-full sm:max-w-2xl h-[92vh] sm:h-auto sm:min-h-[min(560px,85vh)] sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden text-left">

        {/* Cabecera */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 truncate">Nueva receta electrónica</h2>
            <p className="text-[13px] text-gray-700">
              {paso === 5 ? 'Emitida' : `Paso ${paso} de 4`}
            </p>
          </div>
          <button onClick={cerrar} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {paso <= 4 && <BarraPasos actual={paso} />}

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {paso === 1 && (
            <PasoPaciente
              patient={patient} setPatient={setPatient}
              financiador={financiador} setFinanciador={recordarObraSocial}
              setFichaId={setFichaId}
              onGuardarNroAfiliado={recordarNroAfiliado}
            />
          )}
          {paso === 2 && <PasoDiagnostico diagnosis={diagnosis} setDiagnosis={setDiagnosis} />}
          {paso === 3 && (
            <PasoMedicamentos
              nroAfiliado={patient.insuranceNumber}
              renglones={renglones}
              setRenglones={setRenglones}
              dni={patient.dni}
              financiador={financiador}
            />
          )}
          {paso === 4 && (
            <PasoRevision
              patient={patient}
              diagnosis={diagnosis}
              renglones={medicamentosCargados}
              financiador={financiador}
              signature={signature}
              setSignature={setSignature}
              error={error}
            />
          )}
          {paso === 5 && resultado && <PasoExito resultado={resultado} onCerrar={cerrar} />}
        </div>

        {/* Error */}
        {error && paso !== 5 && (
          <div className="mx-5 mb-3 flex items-start gap-2 text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Pie */}
        {paso <= 4 && (
          <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={paso === 1 ? cerrar : () => { setError(''); setPaso(paso - 1); }}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors flex items-center gap-1"
            >
              {paso > 1 && <ChevronLeft className="w-4 h-4" />}
              {paso === 1 ? 'Cancelar' : 'Anterior'}
            </button>

            <div className="flex-1" />

            {paso < 4 ? (
              <button
                onClick={avanzar}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-[#0FA3B1] hover:bg-[#0D8F9C] rounded-xl transition-colors flex items-center gap-1.5"
              >
                Continuar
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={emitir}
                disabled={emitiendo || !signature}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center gap-2"
              >
                {emitiendo
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Emitiendo…</>
                  : <><ShieldCheck className="w-4 h-4" /> Firmar y emitir</>}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ===================================================================
// Paso 1 — Paciente
// ===================================================================
const PasoPaciente: React.FC<{
  patient: any;
  setPatient: (p: any) => void;
  financiador: Financiador | null;
  setFinanciador: (f: Financiador | null) => void;
  setFichaId: (id: number | null) => void;
  onGuardarNroAfiliado: (numero: string) => void;
}> = ({ patient, setPatient, financiador, setFinanciador, setFichaId, onGuardarNroAfiliado }) => {
  const [buscandoOS, setBuscandoOS] = useState(false);
  const [msgOS, setMsgOS] = useState<{ ok: boolean; texto: string } | null>(null);
  const [sugerencia, setSugerencia] = useState('');

  const dniOk = patient.dni.replace(/\D/g, '').length >= 6;

  const traerCobertura = async () => {
    setBuscandoOS(true); setMsgOS(null);
    try {
      const token = localStorage.getItem('token');
      // Tipado explícito: sin esto TypeScript infiere una unión de dos formas
      // distintas de `headers` y no la acepta como RequestInit.
      const auth: RequestInit = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const dni = encodeURIComponent(patient.dni);

      // Dos padrones distintos, los dos gratuitos, en paralelo:
      //   - coberturas (PUCO): qué obra social tiene la persona.
      //   - paciente (Federador): nombre, apellido, sexo, fecha de nacimiento.
      // El Federador está casi vacío —al 7-sep-2026 no encontró a ninguna de
      // las personas con las que se probó— así que se lo trata como un extra:
      // si trae algo, se aprovecha; si no, todo sigue igual que antes.
      const [rCob, rPac] = await Promise.all([
        fetch(`${API_URL}/api/msal/coberturas?dni=${dni}`, auth),
        fetch(`${API_URL}/api/msal/paciente?dni=${dni}`, auth).catch(() => null),
      ]);

      const j = await rCob.json();
      const c = (j.coberturas || [])[0];

      // Solo se completan los campos VACÍOS. Si el profesional ya escribió
      // algo, manda lo suyo: tiene al paciente delante y el padrón puede estar
      // desactualizado.
      let traidos: string[] = [];
      try {
        const jp = rPac ? await rPac.json() : null;
        const p = jp && jp.encontrado ? jp.paciente : null;
        if (p) {
          const cambios: any = {};
          const nombreCompleto = [p.nombre, p.apellido].filter(Boolean).join(' ').trim();
          if (!patient.name.trim() && nombreCompleto) { cambios.name = nombreCompleto; traidos.push('nombre'); }
          if (!patient.birthDate && p.birthDate) { cambios.birthDate = p.birthDate; traidos.push('fecha de nacimiento'); }
          if (!patient.sex && p.gender) {
            const s = String(p.gender).toLowerCase();
            const letra = s === 'female' ? 'F' : s === 'male' ? 'M' : 'X';
            cambios.sex = letra; traidos.push('sexo');
          }
          if (Object.keys(cambios).length) setPatient((prev: any) => ({ ...prev, ...cambios }));
        }
      } catch { /* el Federador es un extra: que no rompa la carga */ }
      if (j.encontrado && c) {
        // El padrón devuelve el nombre legal ("INSTITUTO NACIONAL DE SERVICIOS
        // SOCIALES PARA JUBILADOS Y PENSIONADOS") y nadie la llama así: es
        // PAMI. Cuando el backend reconoce la sigla, se usa esa — es la que el
        // profesional entiende y además la que existe en el catálogo de Qbi2,
        // así que el buscador de abajo la encuentra. Si no la reconoce, se
        // muestra el nombre del padrón, que es correcto aunque sea largo.
        const nombreCorto = c.sigla || c.obraSocial;
        setPatient({ ...patient, healthInsurance: nombreCorto || patient.healthInsurance });
        // Texto de arranque del buscador de abajo, para que el profesional
        // confirme de un clic en vez de tipear.
        setSugerencia(nombreCorto || '');
        const extra = traidos.length ? ` También se completó ${traidos.join(', ')}.` : '';
        setMsgOS({
          ok: true,
          texto: (c.sigla
            ? `${c.sigla} — ${c.obraSocial}. El número de afiliado no figura en el padrón.`
            : `${c.obraSocial}. El número de afiliado no figura en el padrón.`) + extra,
        });
      } else if (traidos.length) {
        // Sin obra social pero con datos de la persona: igual sirvió.
        setMsgOS({ ok: true, texto: `Se completó ${traidos.join(', ')}. No figura obra social en el padrón.` });
      } else {
        setMsgOS({ ok: false, texto: 'No figura en el padrón. Cargala a mano si corresponde.' });
      }
    } catch {
      setMsgOS({ ok: false, texto: 'El padrón no responde. Cargala a mano.' });
    } finally { setBuscandoOS(false); }
  };

  return (
    <div className="space-y-4">
      <p className="text-[15px] text-gray-700">
        Los datos del paciente van impresos en la receta. El sexo es obligatorio por el Decreto 63/2024.
      </p>

      {/* Traer un paciente ya cargado, con su obra social incluida */}
      <BuscadorFicha
        onElegir={(f) => {
          setPatient({
            name: [f.nombre, f.apellido].filter(Boolean).join(' ').trim() || patient.name,
            dni: f.nro_doc || '',
            sex: (f.sexo || '') as any,
            birthDate: f.fecha_nacimiento ? String(f.fecha_nacimiento).slice(0, 10) : '',
            healthInsurance: f.obra_social_nombre || '',
            insuranceNumber: f.nro_afiliado || '',
          });
          // Si la ficha ya tiene guardado el financiador, no hay que volver a
          // elegirlo: es justamente el punto de guardarlo.
          //
          // Se comprueba que el número tenga pinta de id de Qbi2 (van del 1 al
          // 835) y no de RNOS del padrón (seis dígitos). Fichas viejas pueden
          // traer el RNOS ahí — la migración 024 los separa, pero mejor no
          // depender de eso: mandarle a Qbi2 un id que no existe devuelve la
          // cobertura vacía sin ningún error, y el profesional se lo diría al
          // paciente. Ante la duda, que la elija.
          const idQbi2 = Number(f.obra_social_id);
          const pareceDeQbi2 = Number.isInteger(idQbi2) && idQbi2 > 0 && idQbi2 < 10000;
          if (pareceDeQbi2 && f.obra_social_nombre) {
            setFinanciador({ idfinanciador: idQbi2, nombreComercial: f.obra_social_nombre });
          } else {
            setFinanciador(null);
          }
          setFichaId(f.id);
          setMsgOS(null);
          setSugerencia('');
        }}
      />

      <div className="grid grid-cols-2 gap-3">
        <Campo label="Nombre y apellido" className="col-span-2" requerido>
          <input
            className={claseInput}
            value={patient.name}
            onChange={(e) => setPatient({ ...patient, name: e.target.value })}
            placeholder="Como figura en el documento"
          />
        </Campo>

        <Campo label="DNI">
          <input
            className={claseInput}
            value={patient.dni}
            inputMode="numeric"
            onChange={(e) => setPatient({ ...patient, dni: e.target.value })}
            placeholder="Sin puntos"
          />
        </Campo>

        <Campo label="Sexo" requerido>
          <select
            className={claseInput}
            value={patient.sex}
            onChange={(e) => setPatient({ ...patient, sex: e.target.value })}
          >
            <option value="">Elegir…</option>
            <option value="F">Femenino</option>
            <option value="M">Masculino</option>
            <option value="X">X</option>
          </select>
        </Campo>

        <Campo label="Fecha de nacimiento" className="col-span-2">
          <input
            type="date"
            className={claseInput}
            value={patient.birthDate}
            onChange={(e) => setPatient({ ...patient, birthDate: e.target.value })}
          />
        </Campo>

        {/* Traer la obra social del padrón */}
        <div className="col-span-2">
          <button
            type="button"
            onClick={traerCobertura}
            disabled={!dniOk || buscandoOS}
            className="w-full py-2.5 rounded-xl border border-dashed border-[#0FA3B1]/50 bg-[#0FA3B1]/10 text-[#096C76] text-[14px] font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#0FA3B1]/8 transition-colors"
          >
            {buscandoOS ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {buscandoOS ? 'Consultando los padrones…' : 'Traer datos por DNI'}
          </button>
          {!dniOk && (
            <p className="mt-1 text-[12px] text-gray-500 text-center">Escribí el DNI para consultar los padrones</p>
          )}
          {msgOS && (
            <p className={`mt-2 text-[13px] flex items-start gap-1.5 rounded-lg px-2.5 py-1.5 border ${
              msgOS.ok ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                       : 'text-gray-700 bg-gray-50 border-gray-200'
            }`}>
              {msgOS.ok ? <Check className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        : <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
              {msgOS.texto}
            </p>
          )}
        </div>

        <Campo label="Obra social">
          <input
            className={claseInput}
            value={patient.healthInsurance}
            onChange={(e) => setPatient({ ...patient, healthInsurance: e.target.value })}
            placeholder="Particular si no tiene"
          />
        </Campo>

        <Campo label="Nº de afiliado">
          <input
            className={claseInput}
            value={patient.insuranceNumber}
            onChange={(e) => setPatient({ ...patient, insuranceNumber: e.target.value })}
            onBlur={(e) => onGuardarNroAfiliado(e.target.value)}
            placeholder="Del carnet"
          />

          {/* Se ofrece el DNI, no se completa solo.
              En varias obras sociales la credencial ES el documento (IOSCOR,
              por ejemplo), pero en OSDE o Swiss Medical no: son numeraciones
              propias. Completarlo en silencio metería un número de afiliado
              falso en un documento legal, y el rechazo aparecería recién en
              la farmacia. Así el que decide es el profesional. */}
          {!patient.insuranceNumber.trim() && patient.dni.replace(/\D/g, '').length >= 7 && (
            <button
              type="button"
              onClick={() => {
                const soloDni = patient.dni.replace(/\D/g, '');
                setPatient({ ...patient, insuranceNumber: soloDni });
                onGuardarNroAfiliado(soloDni);
              }}
              className="mt-1 text-[12px] text-[#0B7C87] font-semibold text-left active:opacity-60"
            >
              ¿Es el DNI? Usar {patient.dni.replace(/\D/g, '')}
            </button>
          )}
        </Campo>

        <div className="col-span-2">
          <SelectorFinanciador
            elegido={financiador}
            onElegir={setFinanciador}
            textoInicial={sugerencia}
          />
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// Traer un paciente ya cargado en las fichas
//
// Evita volver a tipear nombre, DNI, sexo y obra social en cada receta. Y si
// la ficha ya tiene guardado el financiador, la cobertura queda resuelta sin
// que el profesional elija nada.
// -------------------------------------------------------------------
const BuscadorFicha: React.FC<{ onElegir: (f: any) => void }> = ({ onElegir }) => {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);

  const profId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return null; }
  })();

  const url = useCallback(
    (q: string) => `/api/patient-records/professional/${profId}?q=${encodeURIComponent(q)}&limit=8`,
    [profId]
  );
  const extraer = useCallback((j: any) => (j.patients || []) as any[], []);
  const { resultados, buscando, buscar, limpiar } = useBuscador<any>(url, extraer);

  useEffect(() => {
    const t = setTimeout(() => buscar(texto), 350);
    return () => clearTimeout(t);
  }, [texto, buscar]);

  if (!profId) return null;

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
      <input
        className={`${claseInput} pl-9 bg-gray-50`}
        value={texto}
        onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        placeholder="Buscar entre mis pacientes…"
      />
      {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0B7C87] animate-spin" />}

      {abierto && resultados.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
          {resultados.map((f) => (
            <button
              key={f.id}
              onClick={() => { onElegir(f); setTexto(''); setAbierto(false); limpiar(); }}
              className="w-full text-left px-3 py-2 hover:bg-[#0FA3B1]/8 transition-colors border-b border-gray-50 last:border-0"
            >
              <p className="text-sm text-gray-800">
                {[f.apellido, f.nombre].filter(Boolean).join(', ')}
              </p>
              <p className="text-[12px] text-gray-500">
                {[f.nro_doc && `DNI ${f.nro_doc}`, f.obra_social_nombre].filter(Boolean).join(' · ') || 'Sin datos de cobertura'}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// Elegir la obra social del catálogo de Qbi2
//
// Es opcional. Sirve para una sola cosa, pero importante: sin ella el
// catálogo de medicamentos no puede decir si el plan cubre y con qué
// descuento.
// -------------------------------------------------------------------
const SelectorFinanciador: React.FC<{
  elegido: Financiador | null;
  onElegir: (f: Financiador | null) => void;
  textoInicial: string;
}> = ({ elegido, onElegir, textoInicial }) => {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);

  const url = useCallback((q: string) => `/api/prescriptions/catalogo/financiadores?q=${encodeURIComponent(q)}`, []);
  const extraer = useCallback((j: any) => (j.financiadores || []) as Financiador[], []);
  const { resultados, buscando, aviso, buscar, limpiar } = useBuscador<Financiador>(url, extraer);

  // Cuando el padrón trae un nombre, se arranca la búsqueda con él.
  useEffect(() => {
    if (textoInicial && !elegido) { setTexto(textoInicial); setAbierto(true); }
  }, [textoInicial, elegido]);

  useEffect(() => {
    const t = setTimeout(() => buscar(texto), 350);
    return () => clearTimeout(t);
  }, [texto, buscar]);

  if (elegido) {
    return (
      <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <BadgeCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-emerald-800 truncate">{elegido.nombreComercial}</p>
            <p className="text-[12px] text-emerald-600">Se va a usar para ver la cobertura de cada medicamento</p>
          </div>
        </div>
        <button
          onClick={() => { onElegir(null); setTexto(''); }}
          className="text-[13px] text-emerald-700 hover:underline flex-shrink-0"
        >
          Cambiar
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">
        Confirmá la obra social <span className="text-gray-500 font-normal">— opcional</span>
      </label>
      {/* El relative va acá y no en el contenedor de afuera: ahí adentro
          también viven la etiqueta y el texto de ayuda, y la lupa terminaba
          centrada sobre todo el bloque. Antes se compensaba con un top fijo
          de 30px, que se desalinea apenas la etiqueta pasa a dos líneas. */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          className={`${claseInput} pl-9`}
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)}
          placeholder="Buscá en el listado…"
        />
        {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0B7C87] animate-spin" />}

        {abierto && resultados.length > 0 && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
            {resultados.map((f) => (
              <button
                key={f.idfinanciador}
                onClick={() => { onElegir(f); setAbierto(false); limpiar(); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-[#0FA3B1]/8 transition-colors border-b border-gray-50 last:border-0"
              >
                {f.nombreComercial}
              </button>
            ))}
          </div>
        )}

      </div>

      <p className="mt-1 text-[12px] text-gray-500">
        {aviso || 'Elegirla permite ver si el plan cubre cada medicamento. Sin esto no se muestra cobertura.'}
      </p>
    </div>
  );
};

// ===================================================================
// Paso 2 — Diagnóstico
// ===================================================================
const PasoDiagnostico: React.FC<{ diagnosis: any; setDiagnosis: (d: any) => void }> = ({ diagnosis, setDiagnosis }) => {
  const [texto, setTexto] = useState(diagnosis.description || '');
  const [abierto, setAbierto] = useState(false);

  const urlDiag = useCallback((q: string) => `/api/prescriptions/catalogo/diagnosticos?q=${encodeURIComponent(q)}`, []);
  const extraerDiag = useCallback((j: any) => (j.diagnosticos || []) as DiagnosticoCatalogo[], []);
  const { resultados, buscando, aviso, buscar, limpiar } = useBuscador<DiagnosticoCatalogo>(urlDiag, extraerDiag);

  useEffect(() => {
    const t = setTimeout(() => buscar(texto), 350);
    return () => clearTimeout(t);
  }, [texto, buscar]);

  const elegir = (d: DiagnosticoCatalogo) => {
    setDiagnosis({ code: d.coddiagnostico, description: d.descdiagnostico });
    setTexto(d.descdiagnostico);
    setAbierto(false);
    limpiar();
  };

  return (
    <div className="space-y-4">
      <p className="text-[15px] text-gray-700">
        Buscá el diagnóstico en el nomenclador. Es opcional, pero cargarlo codificado ayuda a que la
        obra social procese la receta sin trabas.
      </p>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          className={`${claseInput} pl-9`}
          value={texto}
          onChange={(e) => { setTexto(e.target.value); setAbierto(true); setDiagnosis({ code: '', description: e.target.value }); }}
          onFocus={() => setAbierto(true)}
          placeholder="Escribí al menos 3 letras…"
        />
        {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0B7C87] animate-spin" />}

        {abierto && resultados.length > 0 && (
          <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
            {resultados.slice(0, 40).map((d) => (
              <button
                key={d.iddiagnostico}
                onClick={() => elegir(d)}
                className="w-full text-left px-3 py-2 hover:bg-[#0FA3B1]/8 transition-colors border-b border-gray-50 last:border-0"
              >
                <p className="text-sm text-gray-800">{d.descdiagnostico}</p>
                <p className="text-[12px] text-gray-500 font-mono">{d.coddiagnostico}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {aviso && (
        <p className="text-[13px] text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{aviso}</p>
      )}

      {diagnosis.code && (
        <div className="flex items-center gap-2 text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
          <BadgeCheck className="w-4 h-4 flex-shrink-0" />
          <span>Codificado como <span className="font-mono font-semibold">{diagnosis.code}</span></span>
        </div>
      )}
    </div>
  );
};

// ===================================================================
// Paso 3 — Medicamentos
// ===================================================================
const PasoMedicamentos: React.FC<{
  renglones: Renglon[];
  setRenglones: (r: Renglon[]) => void;
  dni: string;
  financiador: Financiador | null;
  nroAfiliado: string;
}> = ({ renglones, setRenglones, dni, financiador, nroAfiliado }) => {
  // Qbi2 exige el número de afiliado si se informa la obra social ("EL
  // AFILIADO ES REQUERIDO SI SE INFORMA EL FINANCIADOR"). Sin él la receta
  // sale igual, pero como particular: conviene que el profesional lo sepa
  // antes de firmar y no que se entere el paciente en la farmacia.
  const faltaAfiliado = Boolean(financiador) && !String(nroAfiliado || '').trim();
  const cambiar = (i: number, cambios: Partial<Renglon>) => {
    const copia = [...renglones];
    copia[i] = { ...copia[i], ...cambios };
    setRenglones(copia);
  };

  return (
    <div className="space-y-4">
      <p className="text-[15px] text-gray-700">
        Buscá el medicamento en el catálogo. Al elegirlo se completan solos la droga, la forma
        farmacéutica y la presentación, que el Decreto 63/2024 pide como campos separados.
      </p>

      {financiador && faltaAfiliado ? (
        <p className="text-[13px] text-[#8A4B00] bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-lg px-3 py-2">
          Elegiste <span className="font-semibold">{financiador.nombreComercial}</span>, pero falta
          el N° de afiliado. La receta se va a emitir <span className="font-semibold">como particular</span>,
          sin descuento. Cargalo en el paso 1 si lo tenés.
        </p>
      ) : financiador ? (
        <p className="text-[13px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
          Mostrando la cobertura de <span className="font-semibold">{financiador.nombreComercial}</span>.
        </p>
      ) : (
        <p className="text-[13px] text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          No elegiste obra social, así que no se muestra cobertura. Podés volver al paso 1 y elegirla.
        </p>
      )}

      {renglones.map((r, i) => (
        <RenglonMedicamento
          key={i}
          indice={i}
          renglon={r}
          dni={dni}
          financiador={financiador}
          onCambiar={(c) => cambiar(i, c)}
          onBorrar={renglones.length > 1 ? () => setRenglones(renglones.filter((_, j) => j !== i)) : undefined}
        />
      ))}

      <button
        onClick={() => setRenglones([...renglones, { ...RENGLON_VACIO }])}
        className="w-full py-2.5 rounded-xl border border-dashed border-gray-300 text-gray-700 text-[13px] font-semibold flex items-center justify-center gap-1.5 hover:border-[#0FA3B1]/40 hover:text-[#0B7C87] transition-colors"
      >
        <Plus className="w-4 h-4" /> Agregar otro medicamento
      </button>
    </div>
  );
};

const RenglonMedicamento: React.FC<{
  indice: number;
  renglon: Renglon;
  dni: string;
  financiador: Financiador | null;
  onCambiar: (c: Partial<Renglon>) => void;
  onBorrar?: () => void;
}> = ({ indice, renglon, dni, financiador, onCambiar, onBorrar }) => {
  const [texto, setTexto] = useState(renglon.commercialName || renglon.medication || '');
  const [abierto, setAbierto] = useState(false);

  // El idFinanciador es lo que hace que `tieneCobertura` y `descuento`
  // signifiquen algo. El DNI solo no alcanza.
  const urlMed = useCallback(
    (q: string) =>
      `/api/prescriptions/catalogo/medicamentos?q=${encodeURIComponent(q)}` +
      (financiador ? `&financiador=${encodeURIComponent(financiador.idfinanciador)}` : '') +
      (dni ? `&dni=${encodeURIComponent(dni)}` : ''),
    [dni, financiador]
  );
  const extraerMed = useCallback((j: any) => (j.medicamentos || []) as MedicamentoCatalogo[], []);
  const { resultados, buscando, aviso, buscar, limpiar } = useBuscador<MedicamentoCatalogo>(urlMed, extraerMed);

  useEffect(() => {
    if (renglon.catalogo) return; // ya eligió uno, no seguir buscando
    const t = setTimeout(() => buscar(texto), 350);
    return () => clearTimeout(t);
  }, [texto, buscar, renglon.catalogo]);

  const elegir = (m: MedicamentoCatalogo) => {
    // La potencia a veces viene sólo con la unidad ("mg") y sin número, y a
    // veces ya está adentro de la presentación. En los dos casos pegarla suma
    // ruido: "500 mg comp.rec.x 30 · mg". Se agrega únicamente cuando aporta
    // algo — que tenga un número y no esté repetida.
    const potencia = (m.descripcionPotencia || m.potencia || '').trim();
    const aporta =
      /\d/.test(potencia) &&
      !(m.presentacion || '').toLowerCase().includes(potencia.toLowerCase());

    onCambiar({
      medication: m.nombreDroga || m.nombreProducto,
      commercialName: m.nombreProducto,
      pharmaceuticalForm: m.formaFarmaceutica || '',
      presentation: [m.presentacion, aporta ? potencia : ''].filter(Boolean).join(' · '),
      catalogo: m,
    });
    setTexto(m.nombreProducto);
    setAbierto(false);
    limpiar();
  };

  const soltar = () => {
    onCambiar({ medication: '', commercialName: '', pharmaceuticalForm: '', presentation: '', catalogo: undefined });
    setTexto('');
  };

  return (
    <div className="border border-gray-200 rounded-2xl p-3.5 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <span className="text-[13px] font-semibold text-gray-500">Medicamento {indice + 1}</span>
        {onBorrar && (
          <button onClick={onBorrar} className="p-1 rounded-lg hover:bg-red-50 text-gray-300 hover:text-red-500 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {renglon.catalogo ? (
        // Elegido del catálogo
        <div className="bg-[#0FA3B1]/8 border border-[#0FA3B1]/15 rounded-xl p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900">{renglon.commercialName}</p>
              <p className="text-[13px] text-gray-700">{renglon.medication}</p>
              <p className="text-[13px] text-gray-500 mt-0.5">
                {renglon.pharmaceuticalForm}{renglon.presentation && ` · ${renglon.presentation}`}
              </p>
              <Avisos m={renglon.catalogo} coberturaVerificada={Boolean(financiador)} />
            </div>
            <button onClick={soltar} className="text-[13px] text-[#0B7C87] hover:underline flex-shrink-0">
              Cambiar
            </button>
          </div>
        </div>
      ) : (
        // Buscador
        <div>
          {/* La lupa se ancla solo al campo. Antes este `relative` envolvía
              también los textos de ayuda de abajo, así que top-1/2 la centraba
              sobre todo el bloque y caía por debajo del input. En celular se
              notaba más, porque la ayuda ocupa dos líneas. */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              className={`${claseInput} pl-9`}
              value={texto}
              onChange={(e) => { setTexto(e.target.value); setAbierto(true); }}
              onFocus={() => setAbierto(true)}
              placeholder="Nombre comercial o droga…"
            />
            {buscando && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0B7C87] animate-spin" />}

            {abierto && resultados.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
                {resultados.slice(0, 30).map((m, k) => (
                  <button
                    key={`${m.regNo}-${k}`}
                    onClick={() => elegir(m)}
                    className="w-full text-left px-3 py-2.5 hover:bg-[#0FA3B1]/8 transition-colors border-b border-gray-50 last:border-0"
                  >
                    <p className="text-sm font-medium text-gray-800">{m.nombreProducto}</p>
                    <p className="text-[13px] text-gray-700">{m.nombreDroga}</p>
                    <p className="text-[12px] text-gray-500">
                      {m.formaFarmaceutica}{m.presentacion && ` · ${m.presentacion}`}
                    </p>
                    <Avisos m={m} coberturaVerificada={Boolean(financiador)} />
                  </button>
                ))}
              </div>
            )}

          </div>

          {aviso && <p className="mt-1.5 text-[13px] text-gray-700">{aviso}</p>}
          <p className="mt-1.5 text-[12px] text-gray-500">
            ¿No aparece? Escribilo igual y completá la forma farmacéutica abajo.
          </p>
        </div>
      )}

      {/* Campos que siempre carga el profesional */}
      <div className="grid grid-cols-2 gap-2.5">
        {!renglon.catalogo && (
          <>
            <Campo label="Forma farmacéutica" requerido>
              <input
                className={claseInput}
                value={renglon.pharmaceuticalForm}
                onChange={(e) => onCambiar({ pharmaceuticalForm: e.target.value, medication: renglon.medication || texto })}
                placeholder="Comprimidos, jarabe…"
              />
            </Campo>
            <Campo label="Presentación">
              <input
                className={claseInput}
                value={renglon.presentation}
                onChange={(e) => onCambiar({ presentation: e.target.value })}
                placeholder="500 mg x 30"
              />
            </Campo>
          </>
        )}

        <Campo label="Cantidad">
          <input
            type="number"
            min={1}
            className={claseInput}
            value={renglon.quantity}
            onChange={(e) => onCambiar({ quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
          />
        </Campo>

        <Campo label="Posología">
          <input
            className={claseInput}
            value={renglon.dosage}
            onChange={(e) => onCambiar({ dosage: e.target.value })}
            placeholder="1 cada 8 hs"
          />
        </Campo>

        <Campo label="Duración" className="col-span-2">
          <input
            className={claseInput}
            value={renglon.duration}
            onChange={(e) => onCambiar({ duration: e.target.value })}
            placeholder="7 días"
          />
        </Campo>

        <Campo label="Indicaciones" className="col-span-2">
          <textarea
            rows={2}
            className={`${claseInput} resize-none`}
            value={renglon.notes}
            onChange={(e) => onCambiar({ notes: e.target.value })}
            placeholder="Tomar con las comidas…"
          />
        </Campo>
      </div>
    </div>
  );
};

// ===================================================================
// Paso 4 — Revisión (la preview antes de emitir)
// ===================================================================
const PasoRevision: React.FC<{
  patient: any;
  diagnosis: any;
  renglones: Renglon[];
  financiador: Financiador | null;
  signature: string | null;
  setSignature: (s: string | null) => void;
  error: string;
}> = ({ patient, diagnosis, renglones, financiador, signature, setSignature, error }) => {
  const controlados = renglones.filter(
    (r) => r.catalogo && (r.catalogo.estupefaciente || r.catalogo.psicofarmaco || r.catalogo.requiereDuplicado)
  );

  return (
    <div className="space-y-4">
      {/* Aviso de irreversibilidad */}
      <div className="flex items-start gap-2.5 text-[13px] bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800">
          <p className="font-semibold">Revisá bien antes de emitir.</p>
          <p className="mt-0.5">
            Una vez emitida, la receta queda registrada y no se puede borrar: sólo anular. Este es el
            momento de corregir.
          </p>
        </div>
      </div>

      {/* La receta como va a salir */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-[#0FA3B1] to-[#30B0C7] px-4 py-3">
          <p className="text-white text-[13px] font-semibold tracking-wide uppercase">Receta electrónica</p>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <p className="text-[12px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Paciente</p>
            <p className="text-sm font-semibold text-gray-900">{patient.name}</p>
            <p className="text-[13px] text-gray-700">
              {[
                patient.dni && `DNI ${patient.dni}`,
                patient.sex && ({ F: 'Femenino', M: 'Masculino', X: 'X' } as any)[patient.sex],
                patient.birthDate,
              ].filter(Boolean).join(' · ')}
            </p>
            <p className="text-[13px] text-gray-700 mt-0.5">
              {patient.healthInsurance
                ? `${patient.healthInsurance}${patient.insuranceNumber ? ` · Afiliado ${patient.insuranceNumber}` : ''}`
                : 'Particular'}
            </p>
          </div>

          {diagnosis.description && (
            <div className="border-t border-gray-100 pt-3">
              <p className="text-[12px] uppercase tracking-wide text-gray-500 font-semibold mb-1">Diagnóstico</p>
              <p className="text-sm text-gray-800">{diagnosis.description}</p>
              {diagnosis.code && <p className="text-[12px] text-gray-500 font-mono">{diagnosis.code}</p>}
            </div>
          )}

          <div className="border-t border-gray-100 pt-3">
            <p className="text-[12px] uppercase tracking-wide text-gray-500 font-semibold mb-2">
              Medicamentos ({renglones.length})
            </p>
            <div className="space-y-2.5">
              {renglones.map((r, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">
                      {r.commercialName || r.medication}
                    </p>
                    <span className="text-[13px] text-gray-700 flex-shrink-0">x{r.quantity}</span>
                  </div>
                  {r.commercialName && r.medication !== r.commercialName && (
                    <p className="text-[13px] text-gray-700">{r.medication}</p>
                  )}
                  <p className="text-[13px] text-gray-500">
                    {[r.pharmaceuticalForm, r.presentation].filter(Boolean).join(' · ')}
                  </p>
                  {(r.dosage || r.duration) && (
                    <p className="text-[13px] text-gray-700 mt-1">
                      {[r.dosage, r.duration].filter(Boolean).join(' — ')}
                    </p>
                  )}
                  {r.notes && <p className="text-[13px] text-gray-700 italic mt-0.5">{r.notes}</p>}
                  {r.catalogo && <Avisos m={r.catalogo} coberturaVerificada={Boolean(financiador)} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recordatorio de controlados */}
      {controlados.length > 0 && (
        <div className="flex items-start gap-2.5 text-[13px] bg-orange-50 border border-orange-200 rounded-xl px-3.5 py-3">
          <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
          <p className="text-orange-800">
            {controlados.length === 1 ? 'Un medicamento de esta receta es' : `${controlados.length} medicamentos de esta receta son`}{' '}
            de venta controlada. Revisá que corresponda el tipo de recetario.
          </p>
        </div>
      )}

      {/* Firma */}
      <div className="p-4 border-2 border-[#0FA3B1]/30 rounded-2xl bg-[#0FA3B1]/6">
        <SignaturePad
          onChange={(dataUrl: string | null) => setSignature(dataUrl)}
          errorMessage={error && error.toLowerCase().includes('firm') ? error : undefined}
        />
      </div>
    </div>
  );
};

// ===================================================================
// Paso 5 — Éxito
// ===================================================================
const PasoExito: React.FC<{
  resultado: {
    cuir: string;
    qrCode: string;
    emision?: {
      registrada: boolean;
      cuir: string | null;
      idReceta: string | null;
      pdfOficial: string | null;
      verificador: string | null;
    };
  };
  onCerrar: () => void;
}> = ({ resultado, onCerrar }) => {
  const [copiado, setCopiado] = useState(false);

  // El número que sirve afuera es el que asignó Qbi2. El CUIR nuestro
  // (CAR-…) es una referencia interna y en la farmacia no significa nada,
  // así que se muestra chico y al final.
  const emision = resultado.emision;
  const pdf = emision?.pdfOficial || null;
  const numero = emision?.cuir || emision?.idReceta || null;

  // El menú nativo para compartir existe en celular y casi nunca en escritorio.
  // Se comprueba con typeof y no por verdadero/falso porque TypeScript declara
  // navigator.share como siempre presente, y ahí la comprobación no diría nada.
  const puedeCompartir = typeof navigator.share === 'function';

  const copiar = (texto: string) => {
    navigator.clipboard?.writeText(texto).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }).catch(() => {});
  };

  /**
   * Compartir la receta.
   *
   * En el celular abre el menú del sistema (WhatsApp, mail, lo que tenga),
   * que es como el paciente la recibe en la práctica. En escritorio ese menú
   * no existe, así que se copia el link y se avisa.
   */
  const compartir = async () => {
    if (!pdf) return;
    const texto = `Receta electrónica${numero ? ` N° ${numero}` : ''}`;
    if (puedeCompartir) {
      try {
        await navigator.share({ title: 'Receta electrónica', text: texto, url: pdf });
        return;
      } catch {
        // El usuario canceló el menú: no es un error, no hay nada que avisar.
        return;
      }
    }
    copiar(pdf);
  };

  return (
    <div className="text-center space-y-5 py-4">
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
        <Check className="w-8 h-8 text-emerald-600" />
      </div>

      <div>
        <h3 className="font-bold text-gray-900">Receta emitida</h3>
        <p className="text-sm text-gray-700 mt-1">
          Ya está en el repositorio. El paciente puede retirarla en cualquier farmacia.
        </p>
      </div>

      {numero && (
        <div>
          <p className="text-[12px] uppercase tracking-wide text-gray-600 font-semibold mb-1">
            Nº de receta
          </p>
          <button
            onClick={() => copiar(numero)}
            className="inline-flex items-center gap-2 font-mono text-[17px] font-bold text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-xl px-4 py-2.5 transition-colors break-all"
          >
            {numero}
            {copiado ? <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                     : <Copy className="w-4 h-4 text-gray-500 flex-shrink-0" />}
          </button>
        </div>
      )}

      {/* La receta que vale es el PDF de Qbi2, no el nuestro: es el que la
          farmacia reconoce y el que el paciente recibe por WhatsApp. */}
      {pdf && (
        <div className="space-y-2">
          <a
            href={pdf}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 bg-[#0FA3B1] text-white px-5 py-3.5 rounded-2xl font-semibold text-[16px] active:scale-[0.98] active:bg-[#0D8F9C] transition-all"
          >
            <FileText className="w-5 h-5" />
            Ver la receta
          </a>

          <button
            onClick={compartir}
            className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 text-gray-900 px-5 py-3.5 rounded-2xl font-semibold text-[16px] active:scale-[0.98] active:bg-gray-50 transition-all"
          >
            <Share2 className="w-5 h-5 text-[#0B7C87]" />
            {puedeCompartir ? 'Enviar al paciente' : 'Copiar el link'}
          </button>
        </div>
      )}

      {resultado.qrCode && (
        <img src={resultado.qrCode} alt="Código QR de la receta" className="w-32 h-32 mx-auto rounded-xl border border-gray-200" />
      )}

      {emision && !emision.registrada && (
        <p className="text-[13px] text-[#8A4B00] bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-xl px-3 py-2 text-left">
          Se guardó en ConsultAR pero <span className="font-semibold">no se registró afuera</span>,
          así que la farmacia no va a encontrarla.
        </p>
      )}

      <p className="text-[11px] text-gray-500 font-mono break-all">
        Ref. interna {resultado.cuir}
      </p>

      <button
        onClick={onCerrar}
        className="w-full py-2.5 text-sm font-semibold text-white bg-[#0FA3B1] hover:bg-[#0D8F9C] rounded-xl transition-colors"
      >
        Listo
      </button>
    </div>
  );
};

export default PrescriptionWizard;
